import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  Circle,
  MoreHorizontal,
  User,
  Briefcase,
  ArrowUpRight,
  Trash2,
  Edit3,
  RotateCcw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format, isPast, isToday, isTomorrow, differenceInDays } from 'date-fns';
import type { ExtendedTask } from '@/hooks/useTasks';
import type { TaskPriority, TaskStatus } from '@/types/tasks';

interface TaskListProps {
  tasks: ExtendedTask[];
  onTaskClick: (task: ExtendedTask) => void;
  onComplete: (taskId: string) => void;
  onReopen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onEdit: (task: ExtendedTask) => void;
  onAssign?: (taskId: string, assigneeId: string) => void;
  selectedTasks?: string[];
  onSelectTask?: (taskId: string, selected: boolean) => void;
  showFilters?: boolean;
  className?: string;
}

const priorityConfig: Record<TaskPriority, { label: string; color: string; bg: string }> = {
  low: {
    label: 'Low',
    color: 'text-muted-foreground dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800',
  },
  medium: {
    label: 'Medium',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  high: {
    label: 'High',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
  },
  urgent: {
    label: 'Urgent',
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
  },
};

const statusConfig: Record<TaskStatus, { icon: React.ElementType; label: string; color: string }> = {
  todo: { icon: Circle, label: 'To Do', color: 'text-slate-400' },
  in_progress: { icon: ArrowUpRight, label: 'In Progress', color: 'text-[#2B7FFF]' },
  completed: { icon: CheckCircle2, label: 'Completed', color: 'text-green-500' },
  overdue: { icon: AlertCircle, label: 'Overdue', color: 'text-red-500' },
  cancelled: { icon: Circle, label: 'Cancelled', color: 'text-slate-400' },
};

function formatDueDate(dateString?: string): { text: string; isOverdue: boolean; isToday: boolean } {
  if (!dateString) return { text: 'No due date', isOverdue: false, isToday: false };
  
  const date = new Date(dateString);
  const now = new Date();
  
  if (isToday(date)) return { text: 'Today', isOverdue: false, isToday: true };
  if (isTomorrow(date)) return { text: 'Tomorrow', isOverdue: false, isToday: false };
  
  const daysDiff = differenceInDays(date, now);
  
  if (daysDiff < 0) {
    return { 
      text: `${Math.abs(daysDiff)} day${Math.abs(daysDiff) > 1 ? 's' : ''} ago`, 
      isOverdue: true, 
      isToday: false 
    };
  }
  
  if (daysDiff === 0) {
    return { text: format(date, 'h:mm a'), isOverdue: false, isToday: true };
  }
  
  return { 
    text: format(date, 'MMM d'), 
    isOverdue: false, 
    isToday: false 
  };
}

export function TaskList({
  tasks,
  onTaskClick,
  onComplete,
  onReopen,
  onDelete,
  onEdit,
  onAssign,
  selectedTasks = [],
  onSelectTask,
  className,
}: TaskListProps) {
  const [completingTasks, setCompletingTasks] = useState<Set<string>>(new Set());

  const handleComplete = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTasks((prev) => new Set(prev).add(taskId));
    
    // Animate completion before calling the callback
    setTimeout(() => {
      onComplete(taskId);
      setCompletingTasks((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }, 400);
  };

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-foreground">No tasks found</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Try adjusting your filters or create a new task
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2.5', className)}>
      {tasks.map((task) => {
        const priority = priorityConfig[task.priority];
        const status = statusConfig[task.status];
        const dueDate = formatDueDate(task.dueDate);
        const isSelected = selectedTasks.includes(task.id);
        const isCompleting = completingTasks.has(task.id);
        const StatusIcon = status.icon;

        return (
          <motion.div
            key={task.id}
            layout="position"
            animate={{ opacity: isCompleting ? 0.5 : 1, scale: isCompleting ? 0.98 : 1 }}
            transition={{ duration: 0.15 }}
            onClick={() => onTaskClick(task)}
            className={cn(
              'group relative flex items-center gap-3 rounded-xl border bg-card p-3',
              'cursor-pointer transition-colors hover:border-[#2B7FFF]/30 hover:bg-muted/30',
              'dark:bg-card dark:border-border',
              isSelected && 'border-[#2B7FFF] bg-[#2B7FFF]/5',
              task.status === 'overdue' && 'border-red-200 dark:border-red-800 bg-red-50/40 dark:bg-red-900/10',
              task.status === 'completed' && 'opacity-60'
            )}
          >
              {/* Selection Checkbox */}
              {onSelectTask && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => onSelectTask(task.id, checked as boolean)}
                  />
                </div>
              )}

              {/* Completion Checkbox */}
              <button
                onClick={(e) => task.status === 'completed' ? onReopen(task.id) : handleComplete(task.id, e)}
                className={cn(
                  'flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-all',
                  task.status === 'completed'
                    ? 'bg-green-500 border-green-500'
                    : 'border-slate-300 dark:border-slate-600 hover:border-[#2B7FFF]',
                  task.status === 'overdue' && task.status !== 'completed' && 'border-red-400'
                )}
              >
                {task.status === 'completed' && (
                  <CheckCircle2 className="h-4 w-4 text-white" />
                )}
              </button>

              {/* Priority Indicator */}
              <div
                className={cn(
                  'h-10 w-1 rounded-full',
                  task.priority === 'low' && 'bg-slate-300',
                  task.priority === 'medium' && 'bg-blue-400',
                  task.priority === 'high' && 'bg-orange-400',
                  task.priority === 'urgent' && 'bg-red-500'
                )}
              />

              {/* Task Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <h4 className={cn(
                    'truncate font-medium text-sm text-foreground',
                    task.status === 'completed' && 'line-through text-muted-foreground'
                  )}>
                    {task.title}
                  </h4>
                  {task.recurrence && (
                    <RotateCcw className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
                
                {task.description && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {task.description}
                  </p>
                )}

                {/* Linked Entities */}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {task.linkedContactName && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{task.linkedContactName}</span>
                    </div>
                  )}
                  {task.linkedDealName && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Briefcase className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{task.linkedDealName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Priority Badge */}
              <Badge 
                variant="secondary" 
                className={cn('hidden sm:inline-flex text-[10px]', priority.bg, priority.color)}
              >
                {priority.label}
              </Badge>

              {/* Assignee */}
              {task.assigneeName && (
                <Avatar className="hidden h-7 w-7 sm:flex">
                  <AvatarFallback className="text-xs bg-[#2B7FFF]/10 text-[#2B7FFF]">
                    {task.assigneeName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
              )}

              {/* Due Date */}
              <div className={cn(
                'flex items-center gap-1.5 text-sm whitespace-nowrap',
                dueDate.isOverdue && 'text-red-600 dark:text-red-400',
                dueDate.isToday && !dueDate.isOverdue && 'text-orange-600 dark:text-orange-400',
                !dueDate.isOverdue && !dueDate.isToday && 'text-muted-foreground'
              )}>
                <Calendar className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{dueDate.text}</span>
              </div>

              {/* Status Icon */}
              <div className={cn('hidden md:flex', status.color)}>
                <StatusIcon className="h-5 w-5" />
              </div>

              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onClick={() => onEdit(task)}>
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit Task
                  </DropdownMenuItem>
                  
                  {task.status === 'completed' ? (
                    <DropdownMenuItem onClick={() => onReopen(task.id)}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reopen Task
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => onComplete(task.id)}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark Complete
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={() => onDelete(task.id)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Task
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </motion.div>
        );
      })}
    </div>
  );
}

export default TaskList;

